import { Table, Tooltip } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../components/custom_components/Empty'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import { MeetingTag } from '../../components/custom_components/MeetingTag'
import { PerTableActionButtons } from '../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import { FeatureNameEnum } from '../../graphql/featureToggle/FeatureNameEnum'
import useIsFeatureEnabled from '../../graphql/featureToggle/useIsFeatureEnabled'
import useManagerMeetings from '../../graphql/meeting/hooks/useManagerMeetings'
import { useDateTime } from '../../shared/useDateTime'
import { EmployeeDisplayTinyInfo, MeetingDashboardFragment, MeetingPdfStatus, MeetingStatus, Country } from '../../types'
import ManagerMeetingActionButton from '../dashboard/Table/ManagerMeetingActionButton'
import EmployeeMeetingStatus from './EmployeeMeetingStatus'
import { MeetingCancelModal } from './cancel/MeetingCancelModal'
import { NewMeetingInviteButton } from './invite/NewMeetingInviteButton'
import { MeetingRescheduleButton } from './reschedule/MeetingRescheduleButton'
import { MeetingRescheduleModal } from './reschedule/MeetingRescheduleModal'
import { getConclusionHrefByMeeting } from './util/hrefUtils'
import { ForceConfirmationModal, ForceConfirmationRefetchContext } from './ForceConfirmationModal'
import { showNotification } from '../../components/custom_components/Notification/notification'

interface ManagerMeetingsTableProps {
  employeeList: EmployeeDisplayTinyInfo[]
}

export interface ExtendedMeetingDashboardFragment extends MeetingDashboardFragment {
  isLoggedUserCompanyManagerOfAppraisalSeasonCompany: boolean
  isReplyerActive: boolean
}

export const ManagerMeetingsTable: FC<ManagerMeetingsTableProps> = ({ employeeList }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const {
    isFeatureEnabled: reschedulingEnabled,
    loading: loadingIsFeatureEnabled,
    error: featureError,
  } = useIsFeatureEnabled(FeatureNameEnum.SCHEDULE)
  const { data, error, loading, cancelMeeting } = useManagerMeetings(user.employeeId)
  const { compareDates, formatDateTimeShort } = useDateTime(user)
  const [rescheduleMeeting, setRescheduleMeeting] = useState<ExtendedMeetingDashboardFragment | null>(null)
  const [cancelMeetingData, setCancelMeetingData] = useState<ExtendedMeetingDashboardFragment | null>(null)
  const [forceConfirmationModalData, setForceConfirmationModalData] = useState<ExtendedMeetingDashboardFragment | null>(null)

  const { tableActionButtonsRowWidth } = tableActionButtons()
  /*
  useEffect(() => {
    startPolling(10000)
    return () => {
      stopPolling()
    }
  }, [startPolling, stopPolling])
  */
  if (
    loading ||
    loadingIsFeatureEnabled ||
    error ||
    featureError ||
    reschedulingEnabled === undefined ||
    !data ||
    !data.meetingsByManager
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || loadingIsFeatureEnabled}
        hasNoData={!data || reschedulingEnabled === undefined}
        error={error || featureError}
      />
    )
  }

  const tableData = data.meetingsByManager

  const getConclusionHrefByMeetingId = (id: number) => {
    const meeting = tableData.find((m) => m.id === id)
    return getConclusionHrefByMeeting(meeting)
  }

  const onForceConfirmationModalCancel = () => {
    setForceConfirmationModalData(null)
  }

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('managerMeetings.employee')}</Tooltip>

  const columns = [
    {
      key: 'displayDate',
      title: t('managerMeetings.date'),
      className: 'minWidth-column-l',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (meetingA: ExtendedMeetingDashboardFragment, meetingB: ExtendedMeetingDashboardFragment) =>
        compareDates(meetingA.displayDate, meetingB.displayDate),
      render: (tableData: ExtendedMeetingDashboardFragment) => <PerText>{formatDateTimeShort(tableData.displayDate)}</PerText>,
    },
    {
      key: 'appraisalSeason',
      title: t('managerMeetings.appraisalSeason'),
      className: 'minWidth-column-s',
      sorter: (meetingA: ExtendedMeetingDashboardFragment, meetingB: ExtendedMeetingDashboardFragment) => {
        const comparison = meetingA.appraisalSeason.year - meetingB.appraisalSeason.year
        if (comparison === 0) return compareDates(meetingA.displayDate, meetingB.displayDate)
        else return comparison
      },
      render: (tableData: ExtendedMeetingDashboardFragment) => <PerText>{tableData.appraisalSeason.year}</PerText>,
    },
    {
      key: 'type',
      title: t('managerMeetings.type'),
      className: 'minWidth-column-l',
      render: (tableData: ExtendedMeetingDashboardFragment) => <MeetingTag type={tableData.type} />,
    },
    {
      key: 'employee',
      className: 'minWidth-column-xl',
      title: employeeTableHeader,
      render: (tableData: ExtendedMeetingDashboardFragment) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={tableData.employee.id}
          firstName={tableData.employee.chosenFirstName ? tableData.employee.chosenFirstName : tableData.employee.legalFirstName}
          lastName={tableData.employee.chosenLastName ? tableData.employee.chosenLastName : tableData.employee.legalLastName}
          infoText={tableData.employee.currentEmployeeLevel && t(`employee.contract.level.${tableData.employee.currentEmployeeLevel}`)}
          data-e2e-meeting-table-avatar
        />
      ),
    },
    {
      key: 'status',
      title: t('managerMeetings.status'),
      className: 'minWidth-column-l',
      render: (tableData: ExtendedMeetingDashboardFragment) => (
        <>
          <EmployeeMeetingStatus
            firstName={tableData.employee.chosenFirstName ? tableData.employee.chosenFirstName : tableData.employee.legalFirstName}
            meetingStatus={tableData.status}
            employeePrepared={tableData.employeePrepared}
            justify="start"
          />
        </>
      ),
    },
    {
      key: 'actions',
      title: t('managerMeetings.actions'),
      className: 'minWidth-column-s textAlign-left',
      width: tableActionButtonsRowWidth(3),
      render: (tableData: ExtendedMeetingDashboardFragment) => {
        const statusPending = tableData.status === MeetingStatus.PendingConfirmation
        const isMeetingCancelable = [MeetingStatus.Draft, MeetingStatus.Prepared].includes(tableData.status)
        const pdfSuccess = tableData.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess
        return (
          <>
            <PerTableActionButtons
              variant="pastel"
              justify="start"
              buttons={[
                {
                  showOnlyWhen: !statusPending,
                  reactNode: (
                    <ManagerMeetingActionButton
                      meeting={tableData}
                      disabledTooltip={!tableData.isReplyerActive ? t('managerMeetings.tooltip.inactiveEmployee') : undefined}
                      type="circle"
                    />
                  ),
                },
                {
                  showOnlyWhen: reschedulingEnabled && tableData.status !== MeetingStatus.PendingConfirmation,
                  reactNode: (
                    <MeetingRescheduleButton
                      disabledTooltip={!tableData.isReplyerActive ? t('managerMeetings.tooltip.inactiveEmployee') : undefined}
                      meeting={tableData}
                      onOpenModal={setRescheduleMeeting}
                    />
                  ),
                },
                {
                  showOnlyWhen: isMeetingCancelable,
                  icon: 'close_stroke',
                  color: 'secondary',
                  variant: 'default',
                  tooltip: t('managerMeetings.tooltip.cancel'),
                  onClick: () => setCancelMeetingData(tableData),
                  additionalAttrs: {
                    'data-e2e-manager-meeting-action-button': 'cancel',
                  },
                },
                {
                  showOnlyWhen: statusPending && tableData.isLoggedUserCompanyManagerOfAppraisalSeasonCompany,
                  disabled: tableData.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess,
                  icon: 'chat_stroke',
                  color: 'info',
                  tooltip:
                    tableData.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess
                      ? t('managerMeetings.tooltip.viewPdfDisabled')
                      : t('managerMeetings.tooltip.forceConfirmation'),
                  onClick: () => {
                    setForceConfirmationModalData(tableData)
                  },
                },
                {
                  showOnlyWhen: statusPending,
                  icon: 'view_fill',
                  color: 'primary',
                  tooltip: pdfSuccess ? t('managerMeetings.tooltip.viewPdf') : t('managerMeetings.tooltip.viewPdfDisabled'),
                  asLink: getConclusionHrefByMeetingId(tableData.id),
                  disabled: !pdfSuccess,
                  additionalAttrs: {
                    'data-e2e-manager-meeting-action-button': 'view',
                  },
                },
              ]}
            />
          </>
        )
      },
    },
  ]

  return (
    <>
      {forceConfirmationModalData && (
        <ForceConfirmationModal
          companyId={forceConfirmationModalData.appraisalSeason.companyId}
          country={forceConfirmationModalData.appraisalSeason.country as Country}
          meetingId={forceConfirmationModalData.id}
          onClose={onForceConfirmationModalCancel}
          employeeFirstName={forceConfirmationModalData.employee.chosenFirstName || forceConfirmationModalData.employee.legalFirstName}
          refetchContext={ForceConfirmationRefetchContext.MeetingsLists}
        />
      )}
      {cancelMeetingData && (
        <MeetingCancelModal
          isOpen={!!cancelMeetingData}
          onClose={() => setCancelMeetingData(null)}
          onCancelMeeting={async (reasonForMeetingCancel): Promise<boolean> => {
            const res = await cancelMeeting(cancelMeetingData.id, cancelMeetingData.employee.id, reasonForMeetingCancel)
            if (!res.errors || !res.errors.length) {
              setCancelMeetingData(null)
              showNotification({
                type: 'success',
                message: t('managerMeetings.cancelMeetingModal.notifications.success.title'),
                description: t('managerMeetings.cancelMeetingModal.notifications.success.description'),
              })
            }
            return !!res.errors && !!res.errors.length
          }}
        />
      )}
      {rescheduleMeeting && (
        <MeetingRescheduleModal
          meeting={rescheduleMeeting}
          onCancel={() => setRescheduleMeeting(null)}
          visible={!!rescheduleMeeting}
          reviewerId={user.employeeId}
        />
      )}
      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        rowClassName={() => 'minHeight-row'}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <>
              {employeeList.length !== 0 && (
                <PerEmpty
                  icon="chatNo_stroke"
                  heading={t('meetingView.managerMeetings.noMeetingsTitle')}
                  info={t('meetingView.managerMeetings.noMeetingsText')}
                  button={<NewMeetingInviteButton employeeList={employeeList} btnType="normal" />}
                />
              )}
              {employeeList.length === 0 && (
                <PerEmpty icon="chatNo_stroke" heading={t('meetingView.managerMeetings.noMeetingsNoEmployeesText')} />
              )}
            </>
          ),
        }}
        data-e2e-manager-meetings-table
      />
    </>
  )
}
